import { useQuery, useMutation } from '@tanstack/react-query'
import {
  getAssistantById,
  getDeployedVersion,
  getVersions,
  createAssistant,
  deleteAssistants,
  createVersion,
  deployVersion,
  deleteVersion,
} from '@/api/assistant'
import { getUserFolders } from '@/api/folder'
import { toast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { queryClient } from '@/main'

export const useAssistantQuery = (assistantId: string) => {
  return useQuery({
    queryKey: ['assistant', assistantId],
    queryFn: () => getAssistantById(assistantId),
    enabled: !!assistantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export const useAssistantVersionsQuery = (assistantId: string) => {
  return useQuery({
    queryKey: ['assistant-versions', assistantId],
    queryFn: () => getVersions(assistantId),
    enabled: !!assistantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export const useDeployedVersionQuery = (assistantId: string) => {
  return useQuery({
    queryKey: ['deployed-version', assistantId],
    queryFn: () => getDeployedVersion(assistantId),
    enabled: !!assistantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export const useFoldersQuery = () => {
  return useQuery({
    queryKey: ['folders'],
    queryFn: () =>
      getUserFolders({
        page: 1,
        items_per_page: 100,
      }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })
}

export const useCreateAssistantMutation = () => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: createAssistant,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['ai-hub'] })
      toast({
        title: t(tokens.assistant.createAssistantSuccess.title),
        description: t(tokens.assistant.createAssistantSuccess.description),
      })
      return response
    },
    onError: () => {
      toast({
        title: t(tokens.assistant.createAssistantError.title),
        description: t(tokens.assistant.createAssistantError.description),
        variant: 'destructive',
      })
    },
  })
}

export const useDeleteAssistantMutation = () => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: deleteAssistants,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-hub'] })
      toast({
        title: t(tokens.assistant.deleteAssistantSuccess.title),
        description: t(tokens.assistant.deleteAssistantSuccess.description),
      })
    },
    onError: () => {
      toast({
        title: t(tokens.assistant.deleteAssistantError.title),
        description: t(tokens.assistant.deleteAssistantError.description),
        variant: 'destructive',
      })
    },
  })
}

export const useCreateVersionMutation = () => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({
      assistantId,
      params,
    }: {
      assistantId: string
      params: CreateVersionRequest
    }) => createVersion(assistantId, params),
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({
        queryKey: ['assistant-versions', assistantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['deployed-version', assistantId],
      })
      toast({
        title: t(tokens.assistant.createVersionSuccess.title),
        description: t(tokens.assistant.createVersionSuccess.description),
      })
    },
    onError: () => {
      toast({
        title: t(tokens.assistant.createVersionError.title),
        description: t(tokens.assistant.createVersionError.description),
        variant: 'destructive',
      })
    },
  })
}

export const useDeployVersionMutation = () => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({
      assistantId,
      versionId,
    }: {
      assistantId: string
      versionId: string
    }) => deployVersion(assistantId, versionId),
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({
        queryKey: ['assistant-versions', assistantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['deployed-version', assistantId],
      })
      toast({
        title: t(tokens.assistant.deployVersionSuccess.title),
        description: t(tokens.assistant.deployVersionSuccess.description),
      })
    },
    onError: () => {
      toast({
        title: t(tokens.assistant.deployVersionError.title),
        description: t(tokens.assistant.deployVersionError.description),
        variant: 'destructive',
      })
    },
  })
}



export const useDeleteVersionMutation = () => {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: ({
      assistantId,
      versionId,
    }: {
      assistantId: string
      versionId: string
    }) => deleteVersion({ assistant_id: assistantId, version_id: versionId }),
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({
        queryKey: ['assistant-versions', assistantId],
      })
      queryClient.invalidateQueries({
        queryKey: ['deployed-version', assistantId],
      })
      toast({
        title: t(tokens.assistant.deleteVersionSuccess.title),
        description: t(tokens.assistant.deleteVersionSuccess.description),
      })
    },
    onError: () => {
      toast({
        title: t(tokens.assistant.deleteVersionError.title),
        description: t(tokens.assistant.deleteVersionError.description),
        variant: 'destructive',
      })
    },
  })
}
